import { FC, useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import useLocale from '@/utils/useLocale'
import { Space, Select, Button, AutoComplete, Spin, Empty, Tooltip } from '@arco-design/web-react'
import { AnimatePresence, motion } from 'framer-motion'
import { SearchTypes } from '@/types/common'
import { IconClose, IconSearch } from '@arco-design/web-react/icon'
import styles from './index.module.less'
import cx from 'classnames'
import { getSearchApi } from '@/api/common'
import type {
  SearchReq,
  SearchInfluencersRes,
  SearchVideosRes,
  SearchProductsRes,
  SearchLivesRes,
  SearchShopsRes,
  SearchTagsRes
} from '@/types/api'
import EchoNextImage from '@/components/EchoNextImage'
import IconUser from '@/assets/user2x.png'
import IconVideo from '@/assets/video-play2x.png'
import IconProduct from '@/assets/shop2x.png'
import Link from 'next/link'
import { BLUR_CODE, SUCCESS_CODE } from '@/utils/constants'
import { useDebounceFn, useSetState, useUpdateEffect } from 'ahooks'
import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import _ from 'lodash'
import searchHistoryCache from '@/utils/searchHistoryCache'
import IconTranslateBlue from '@/assets/ic_translate_blue_svg.svg'
import { getTranslateApi } from '@/api/common'
import { useSelector } from 'react-redux'
import { StoreState } from '@/store'
import useRegionOptions from '@/hooks/useRegionOptions'
import FlagIcon from '@/components/FlagIcon'
import UploadImage from './upload-image'
import { MdMonochromePhotos } from 'react-icons/md'

type Props = {
  theme?: 'light' | 'dark'
  defaultType?: SearchTypes
  defaultValue?: string
  onSearch?: (value: string) => void
  setCurrentKeyword?: (keyword: string) => void
  autoJumpWhenTypeChange?: boolean
  inputClassName?: string
  searchClassName?: string
  searchButtonClassName?: string
  translateClassName?: string
  onSelect?: () => void
  isGlobal?: boolean
  autoFocused?: boolean
  visible?: boolean
  resetFlag?: number | null
  searchByImage?: boolean
  onImageSearch?: (file: string, keyword: string) => void
  imageSearchLoading?: boolean
  onImageSearchClose?: () => void
}

export function isDetailPage(pathname: string) {
  const regex = /\[id\]/
  if (regex.test(pathname)) {
    return true
  }
  return false
}

const Search: FC<Props> = (props) => {
  const {
    theme = 'dark',
    defaultType = SearchTypes.Influencer,
    defaultValue = '',
    onSearch,
    setCurrentKeyword,
    autoJumpWhenTypeChange = false,
    inputClassName,
    searchClassName,
    searchButtonClassName,
    translateClassName,
    onSelect,
    isGlobal,
    autoFocused,
    visible,
    resetFlag,
    searchByImage = false,
    onImageSearch,
    imageSearchLoading,
    onImageSearchClose
  } = props
  const t = useLocale()
  const router = useRouter()
  const [type, setType] = useState(defaultType)
  const [value, setValue] = useState(defaultValue)
  const [fetching, setFetching] = useState(true)
  const [searchResult, setSearchResult] = useState<
    SearchInfluencersRes | SearchVideosRes | SearchProductsRes | SearchLivesRes | SearchShopsRes | SearchTagsRes
  >([])
  const [isFocus, setIsFocus] = useState(false)
  const [historyMap, setHistoryMap] = useSetState(searchHistoryCache.defaultHistory)
  const [translateLoading, setTranslateLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isSearchByImageModalVisible, setIsSearchByImageModalVisible] = useState(false)

  const configs = [
    {
      id: SearchTypes.Influencer,
      title: t['navbar.search.result.influencer'],
      placeholder: t['search.placeholder.influencer'],
      listRouter: '/influencers',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchInfluencersRes>('influencers', params, config)
      },
      render: () => {
        return (searchResult as SearchInfluencersRes).map((influencer, index) => {
          return (
            <div
              key={index}
              onClick={() => {
                onSelect?.()
                router.push(`/influencers/${influencer.influencer_id}`).then(() => {
                  // if (isDetailPage(router.pathname)) {
                  //   router.reload()
                  // }
                })
              }}
              className={styles.resultItem}
            >
              <div className={styles.cover}>
                <EchoNextImage
                  src={influencer.avatar_url}
                  width={44}
                  height={44}
                  style={{ borderRadius: '50%' }}
                  alt="avatar"
                  errorSrc={IconUser.src}
                />
              </div>
              <Space direction="vertical" size={2}>
                {influencer.highlight ? (
                  <div className={styles.title} dangerouslySetInnerHTML={{ __html: influencer.highlight }}></div>
                ) : (
                  <div className={styles.title}>{influencer.influencer_name}</div>
                )}

                <Space>
                  <FlagIcon region={influencer.region} />
                  <div className={styles.subtitle}>@{influencer.unique_id}</div>
                </Space>
                <div className={styles.fans}>
                  <div>
                    {t['workplace.column.followers']}: {influencer?.follower_count}
                  </div>
                </div>
              </Space>
            </div>
          )
        })
      }
    },
    {
      id: SearchTypes.Video,
      title: t['navbar.search.result.videos'],
      placeholder: t['search.placeholder.video'],
      listRouter: '/videos',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchVideosRes>('videos', params, config)
      },
      render: () => {
        return (searchResult as SearchVideosRes).map((video, index) => {
          return (
            <div
              key={index}
              className={styles.resultItem}
              onClick={() => {
                onSelect?.()
                router.push(`/videos/${video.video_id}`).then(() => {
                  // if (isDetailPage(router.pathname)) {
                  //   router.reload()
                  // }
                })
              }}
            >
              <div className={styles.cover}>
                <EchoNextImage
                  src={video.cover_url}
                  width={44}
                  height={44}
                  style={{ borderRadius: 8 }}
                  alt="cover"
                  errorSrc={IconVideo.src}
                />
              </div>
              <Space direction="vertical" size={2}>
                {video.highlight ? (
                  <div className={styles.title} dangerouslySetInnerHTML={{ __html: video.highlight }}></div>
                ) : (
                  <div className={styles.title}>{video.video_title}</div>
                )}

                <div className={styles.subtitle}>
                  {t['navbar.search.result.videoCount']}
                  {video.views_count}
                </div>
              </Space>
            </div>
          )
        })
      }
    },
    {
      id: SearchTypes.Product,
      title: t['navbar.search.result.products'],
      placeholder: t['search.placeholder.product'],
      listRouter: '/products',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchProductsRes>('products', params, config)
      },
      render: () => {
        return (searchResult as SearchProductsRes).map((product, index) => (
          <div
            key={index}
            className={styles.resultItem}
            onClick={() => {
              onSelect?.()
              router.push(`/products/${product.product_id}`).then(() => {
                // if (isDetailPage(router.pathname)) {
                //   router.reload()
                // }
              })
            }}
          >
            <div className={styles.cover}>
              <EchoNextImage
                src={product.cover_url}
                width={44}
                height={44}
                style={{ borderRadius: 4 }}
                alt="cover"
                errorSrc={IconProduct.src}
              />
            </div>
            <Space direction="vertical" size={2}>
              {product?.highlight ? (
                <div className={styles.title} dangerouslySetInnerHTML={{ __html: product?.highlight }}></div>
              ) : (
                <div className={styles.title}>{product.product_name}</div>
              )}
              <div className={styles.subtitle}>
                {t['navbar.search.result.sales']}
                {product.total_sale_cnt}
              </div>
            </Space>
          </div>
        ))
      }
    },
    {
      id: SearchTypes.Live,
      title: t['navbar.search.result.lives'],
      placeholder: t['search.placeholder.live'],
      listRouter: '/live',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchLivesRes>('lives', params, config)
      },
      render: () => {
        return (searchResult as SearchLivesRes).map((live, index) => (
          <div
            key={index}
            className={styles.resultItem}
            onClick={() => {
              onSelect?.()
              router.push(`/live/${live.live_id}`).then(() => {
                // if (isDetailPage(router.pathname)) {
                //   router.reload()
                // }
              })
            }}
          >
            <div className={styles.cover}>
              <EchoNextImage
                src={live.cover_url}
                width={44}
                height={44}
                style={{ borderRadius: 4 }}
                alt="cover"
                errorSrc={IconVideo.src}
              />
            </div>
            <Space direction="vertical" size={2}>
              {live.highlight ? (
                <div className={styles.title} dangerouslySetInnerHTML={{ __html: live.highlight }}></div>
              ) : (
                <div className={styles.title}>{live.live_title}</div>
              )}
              <div className={styles.subtitle}>
                @{live.influencer.unique_id}
                <span>{live.start_time}</span>
              </div>
            </Space>
          </div>
        ))
      }
    },
    {
      id: SearchTypes.Shop,
      title: t['search.type.shop'],
      placeholder: t['search.placeholder.shop'],
      listRouter: '/shops',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchLivesRes>('sellers', params, config)
      },
      render: () => {
        return (searchResult as SearchShopsRes).map((shop, index) => (
          <div
            key={index}
            className={styles.resultItem}
            onClick={() => {
              onSelect?.()
              router.push(`/shops/${shop.seller_id}`).then(() => {
                // if (isDetailPage(router.pathname)) {
                //   router.reload()
                // }
              })
            }}
          >
            <div className={styles.cover}>
              <EchoNextImage
                src={shop.cover_url}
                width={44}
                height={44}
                style={{ borderRadius: 4 }}
                alt="cover"
                errorSrc={IconProduct.src}
              />
            </div>
            <Space direction="vertical" size={2}>
              {shop?.highlight ? (
                <div className={styles.title} dangerouslySetInnerHTML={{ __html: shop?.highlight }}></div>
              ) : (
                <div className={styles.title}>{shop.seller_name}</div>
              )}
              <div className={styles.subtitle}>
                {t['product.detail.30.sales']} {shop.sale_30d_cnt}
              </div>
            </Space>
          </div>
        ))
      }
    },
    {
      id: SearchTypes.Hashtag,
      title: t['video.detail.hashTag'],
      placeholder: t['search.placeholder.hashtags'],
      listRouter: '/hashtags',
      api: (params: SearchReq, config?: AxiosRequestConfig) => {
        return getSearchApi<SearchTagsRes>('tags', params, config)
      },
      render: () => {
        return (searchResult as SearchTagsRes).map((tag, index) => (
          <div
            key={tag.tag_id}
            className={styles.resultItem}
            onClick={() => {
              onSelect?.()
              router.push(`/hashtags/${tag.tag_id}`).then(() => {
                // if (isDetailPage(router.pathname)) {
                //   router.reload()
                // }
              })
            }}
          >
            <Space direction="vertical" size={2}>
              {tag.highlight ? (
                <div className={styles.title} dangerouslySetInnerHTML={{ __html: tag.highlight }}></div>
              ) : (
                <div className={styles.title}>{tag.tag_title}</div>
              )}

              <div className={styles.subtitle}>
                {t['influencer.column.videos_count']} {tag.video_count}
              </div>
            </Space>
          </div>
        ))
      }
    }
  ]

  const currentConfig = configs.find((item) => item.id === type)!
  const { region } = useSelector((state: StoreState) => state.ssr)
  const { regionOptions } = useRegionOptions()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const activeRegion = regionOptions.find((item) => item.region === region)!

  const handleSearch = () => {
    if (onSearch) {
      onSearch(value)
      value.trim() && updateCurrentHistory(value)
      if (isGlobal) {
        router.push({
          pathname: currentConfig.listRouter,
          query: { keyword: value, isGlobalSearch: isGlobal }
        })
      }
      return
    }

    if (value.trim() === '') {
      return
    }

    updateCurrentHistory(value)
    router.push({
      pathname: currentConfig.listRouter,
      query: { keyword: value }
    })
  }

  useEffect(() => {
    setCurrentKeyword?.(value)
  }, [value])

  useEffect(() => {
    if (autoFocused && inputRef?.current && visible) {
      setTimeout(() => {
        inputRef?.current?.focus()
      })
    }
  }, [autoFocused, visible])

  const updateCurrentHistory = (keyword: string) => {
    setHistoryMap({
      [currentConfig.id]: [
        keyword.trim(),
        ...historyMap[currentConfig.id].filter((item) => item !== keyword.trim())
      ].slice(0, 5)
    })
  }

  const debounceFetch = useDebounceFn(
    (keyword: string, abortController: AbortController) => {
      const currentParams: any = { keyword }
      const order = router?.query?.order
      if (order) {
        currentParams.order = order
      }
      setFetching(true)
      currentConfig
        .api(currentParams, { signal: abortController.signal })
        .then((res) => {
          if ([SUCCESS_CODE, BLUR_CODE].includes(res.data.code)) {
            isFocus && updateCurrentHistory(keyword)
            setSearchResult(res.data.data)
            setFetching(false)
            return
          }
          return Promise.reject()
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            return
          }
          setSearchResult([])
          setFetching(false)
        })
    },
    { wait: 400 }
  )

  const renderResult = () => {
    if (!value.trim()) {
      return null
    }

    if (fetching) {
      return (
        <Spin
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 100
          }}
        />
      )
    }

    return searchResult.length ? currentConfig.render() : <Empty style={{ height: 100 }} />
  }

  useEffect(() => {
    if (value.trim() === '') {
      setSearchResult([])
      setFetching(true)
      return
    }

    const abortController = new AbortController()
    debounceFetch.run(value, abortController)

    return () => {
      abortController.abort()
    }
  }, [value])

  useEffect(() => {
    if (historyMap === searchHistoryCache.defaultHistory) {
      return
    }

    searchHistoryCache.setCache(historyMap)
  }, [historyMap])

  const historyVisible = isFocus && !value

  useEffect(() => {
    if (historyVisible) {
      setHistoryMap(searchHistoryCache.getCache())
    }
  }, [historyVisible])

  const renderHistory = () => {
    const currentHistory = historyMap[currentConfig.id]

    if (_.isEmpty(currentHistory)) {
      return null
    }

    return (
      <div>
        <div className={styles.historyHeader}>
          <span className={styles.historyTitle}>{t['search.search_history']}</span>
          <span
            className={styles.historyClear}
            onClick={() => {
              setHistoryMap({
                [currentConfig.id]: []
              })
            }}
          >
            {t['search.clear_history']}
          </span>
        </div>
        <div>
          {currentHistory.map((text, index) => (
            <div
              key={index}
              className={styles.historyItem}
              onClick={() => {
                setValue(text)
              }}
            >
              <span className={styles.historyItemText}>{text}</span>
              <span
                className={styles.historyDelete}
                onClick={(e) => {
                  e.stopPropagation()
                  setHistoryMap({
                    [currentConfig.id]: currentHistory.filter((item) => item !== text)
                  })
                }}
              >
                <IconClose />
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const onTranslate = () => {
    if (translateLoading || !value) return
    setTranslateLoading(true)
    getTranslateApi({ keyword: value })
      .then((res) => {
        if ([SUCCESS_CODE, BLUR_CODE].includes(res.data.code)) {
          setValue(res.data.data.to)
          setIsFocus(true)
        }
      })
      .finally(() => {
        setTranslateLoading(false)
      })
  }

  useEffect(() => {
    if (resetFlag) {
      setValue('')
    }
  }, [resetFlag])

  return (
    <AnimatePresence mode="wait">
      {isSearchByImageModalVisible ? (
        <motion.div
          key="upload-image"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            duration: 0.3
          }}
          className="flex justify-center w-full"
        >
          <UploadImage
            onImageSearch={onImageSearch}
            onClose={() => {
              setIsSearchByImageModalVisible(false)
              onImageSearchClose?.()
            }}
            imageSearchLoading={imageSearchLoading || false}
          />
        </motion.div>
      ) : (
        <motion.div
          key="search"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
            duration: 0.3
          }}
          className={cx(styles.search, { [styles.darkSearch]: theme === 'dark' }, searchClassName)}
        >
          <Select
            value={type}
            dropdownMenuClassName={styles.dropdownMenuSearch}
            className={styles.select}
            onChange={(val) => {
              setType(val)
              setValue('')

              if (autoJumpWhenTypeChange) {
                const nextConfig = configs.find((item) => item.id === val)!
                router.push({
                  pathname: nextConfig.listRouter
                })
              }
            }}
            triggerProps={{
              className: styles.selectDropdown,
              autoAlignPopupWidth: false,
              position: 'bl'
            }}
          >
            {configs.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.title}
              </Select.Option>
            ))}
          </Select>
          <AutoComplete
            inputProps={{
              ref: (node: HTMLInputElement) => {
                // @ts-expect-error InputProps类型不支持ref，但实际功能正常
                inputRef.current = node
              }
            }}
            value={value}
            onChange={(val, option) => {
              setValue(val)
            }}
            onFocus={() => {
              setIsFocus(true)
            }}
            onBlur={() => {
              setIsFocus(false)
            }}
            placeholder={currentConfig.placeholder}
            onPressEnter={handleSearch}
            className={cx(styles.input, inputClassName)}
            triggerProps={{
              popupVisible: isFocus,
              className: styles.popup,
              unmountOnExit: false,
              onClickOutside: () => {
                setIsFocus(false)
              }
            }}
            loading={translateLoading}
          >
            <AutoComplete.OptGroup key="key">
              {!value && isFocus ? renderHistory() : renderResult()}
            </AutoComplete.OptGroup>
          </AutoComplete>
          <div className={cx(styles.translateWrap, { [styles.darkTranslate]: theme === 'dark' })} onClick={onTranslate}>
            <Tooltip content={`${t['common.icon.translate']} ${t['common.to']} ${activeRegion.lang}`}>
              {
                <div
                  className={cx(
                    styles.iconBlurred,
                    {
                      [styles.iconFocused]: isFocus,
                      [styles.iconUnfocused]: !isFocus,
                      [styles.iconSelected]: translateLoading
                    },
                    translateClassName
                  )}
                >
                  <IconTranslateBlue />
                  {t['common.icon.translate']}
                  {/* {theme === 'dark' ? <IconTranslateBlue /> : <IconTranslateBlue />} */}
                </div>
              }
            </Tooltip>
          </div>
          {searchByImage && (
            <div className="flex items-center justify-center cursor-pointer bg-white pr-2">
              <Button
                size="small"
                onClick={() => setIsSearchByImageModalVisible(true)}
                icon={<MdMonochromePhotos className="text-white text-lg" />}
                className="rounded-lg bg-gradient-to-r from-[#655aec] to-[#a78bfa] text-white border-none transition-all duration-300 hover:shadow-lg hover:shadow-purple-400/40 hover:translate-y-[-1px] hover:brightness-110 relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-white/10 before:opacity-0 hover:before:opacity-100 before:transition-opacity"
              >
                <span className="text-white ">{t['search_by_image']}</span>
              </Button>
            </div>
          )}
          <Button
            className={cx(styles.btn, searchButtonClassName)}
            type="primary"
            icon={<IconSearch />}
            onClick={handleSearch}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Search
